import axios, { RawAxiosRequestHeaders } from 'axios';
import Cookies from 'js-cookie';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { isPast, isValid } from 'date-fns';
import { parseUserAgent } from 'react-device-detect';

import { ACCESS_TOKEN, REFRESH_TOKEN } from '../utils/constants';
import { getLanguage } from '../utils';

import { AccountService } from './Account';

import packageJson from '../../package.json';

import { onForbidden } from '../App';

const axiosInstances: any = {};

const apiUrl = process.env.REACT_APP_API_URL || '';

const userAgent = parseUserAgent(window.navigator.userAgent);

const { browser, os } = userAgent;

const DEFAULT_HEADERS: RawAxiosRequestHeaders = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
	'Accept-Language': getLanguage(),
	'x-app-user-agent': `${browser?.name} ${browser?.version}`,
	'x-app-system': `${os?.name} ${os?.version}`,
	'x-app-build-version': packageJson.version,
};

type ApiRequestProps = {
	version?: string;
	auth?: boolean;
};
const THOUSAND = 1000;
const AUTHORIZATION_ERROR = 401;

type customJwtPayload = JwtPayload & { exp: string };

export const onSuccess = function (response: any) {
	return response.data;
};

export const onError = function (error: any) {
	if (error.response?.status === AUTHORIZATION_ERROR) {
		onForbidden();
	}
	throw error;
};

export class ApiRequest {
	static getInstance({ version = 'v1', auth }: ApiRequestProps) {
		let headers = DEFAULT_HEADERS;

		if (auth) {
			headers = this.checkToken(headers);
		}

		if (!axiosInstances[version] || auth) {
			const instance = axios.create({
				baseURL: `${apiUrl}/${version}`,
				headers,
			});

			instance.interceptors.response.use(onSuccess, onError);

			axiosInstances[version] = instance;
		}
		return axiosInstances[version];
	}

	static getParsedTokenCookies() {
		const access_token = Cookies.get(ACCESS_TOKEN);
		const refresh_token = Cookies.get(REFRESH_TOKEN);

		if (!access_token || !refresh_token) return {};

		return { access_token, refresh_token };
	}

	static checkToken(headers: RawAxiosRequestHeaders) {
		const tokens = this.getParsedTokenCookies();

		const { access_token, refresh_token } = tokens;

		if (access_token && refresh_token) {
			const decodedAccessToken = jwtDecode<customJwtPayload>(access_token);
			const accessTokenExpiration = decodedAccessToken.exp * THOUSAND;

			const decodedRefreshToken = jwtDecode<customJwtPayload>(refresh_token);
			const refreshTokenExpiration = decodedRefreshToken.exp * THOUSAND;

			const isValidDates = isValid(accessTokenExpiration) && isValid(refreshTokenExpiration);

			if (isValidDates) {
				if (isPast(accessTokenExpiration) && !isPast(refreshTokenExpiration)) {
					AccountService.refreshToken({
						access_token,
						refresh_token,
					}).then((data: any) => {
						if (data?.payload) {
							headers = {
								...headers,
								Authorization: `Bearer ${data.payload?.access_token}`,
							};
							return headers;
						}
					});
				} else if (!isPast(accessTokenExpiration)) {
					headers = {
						...headers,
						Authorization: `Bearer ${access_token}`,
					};

					return headers;
				}
			}
		}

		onForbidden();

		return headers;
	}
}
